import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CareTask, CareTaskCategory } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "./PageHeader";

interface Props {
  premiseId: string;
  rooms: { id: string; name?: string }[];
}

const CATEGORY_LABEL: Record<CareTaskCategory, string> = {
  assistance: "Assistance",
  water: "Water",
  restroom: "Restroom",
  pain: "Pain",
  comfort: "Comfort",
  other: "Other",
};

const POLL_MS = 8000;

/** Relative "x min ago" for a timestamp, computed against a live clock. */
function ago(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

/**
 * Live staff queue of resident service requests. Polls every 8 s. Shows open +
 * acknowledged calls prominently (newest first), with Acknowledge / Resolve
 * actions; resolved calls are hidden unless "Show resolved" is on. Gated by
 * `canHandleServiceRequests` at the mount site (and on the server).
 */
export default function ServiceRequestQueue({ premiseId, rooms }: Props) {
  const [tasks, setTasks] = useState<CareTask[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const roomLabel = useCallback(
    (id: string) => {
      const r = rooms.find((x) => x.id === id);
      return r?.name ? `${r.name} (${id})` : id;
    },
    [rooms]
  );

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listCareTasks(premiseId);
      setTasks(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    void refresh();
    timer.current = setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, POLL_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [refresh]);

  const act = async (task: CareTask, status: CareTask["status"]) => {
    setBusyId(task.id);
    setError(null);
    try {
      await foyerApi.setCareTaskStatus(premiseId, task.id, status);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const visible = tasks.filter((t) =>
    showResolved ? true : t.status !== "resolved"
  );
  const openCount = tasks.filter((t) => t.status === "open").length;

  return (
    <Stack spacing={2}>
      <PageHeader
        eyebrow="Care"
        title="Service requests"
        actions={
          <>
            {openCount > 0 && (
              <Chip
                size="small"
                color="error"
                label={`${openCount} open`}
                data-testid="requests-open-count"
              />
            )}
            {loading && <CircularProgress size={16} />}
          </>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => void refresh()}
                data-testid="requests-refresh"
              >
                Refresh
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showResolved}
                    onChange={(e) => setShowResolved(e.target.checked)}
                    data-testid="requests-show-resolved"
                  />
                }
                label="Show resolved"
              />
            </Stack>

            {visible.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No active service requests.
              </Typography>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" data-testid="requests-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Room</TableCell>
                      <TableCell>Request</TableCell>
                      <TableCell>Raised</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.map((t) => (
                      <TableRow key={t.id} data-testid={`request-row-${t.id}`}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {roomLabel(t.roomId)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {CATEGORY_LABEL[t.category]}
                            </Typography>
                            {t.note && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t.note}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {ago(t.createdAt, now)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant={
                              t.status === "open" ? "filled" : "outlined"
                            }
                            color={
                              t.status === "open"
                                ? "error"
                                : t.status === "acknowledged"
                                  ? "warning"
                                  : "success"
                            }
                            label={t.status}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            {t.status === "open" && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CheckCircleIcon />}
                                disabled={busyId === t.id}
                                onClick={() => void act(t, "acknowledged")}
                                data-testid={`request-ack-${t.id}`}
                              >
                                Acknowledge
                              </Button>
                            )}
                            {t.status !== "resolved" && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<DoneAllIcon />}
                                disabled={busyId === t.id}
                                onClick={() => void act(t, "resolved")}
                                data-testid={`request-resolve-${t.id}`}
                              >
                                Resolve
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
